import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Cell, useBlockLayout, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { getCreditChargesAsync } from "src/api/credit/credit-api";
import { ContractCreditBucket, CreditCharge } from "src/api/credit/credit-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, BaseTooltip } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import ScrollShadowProvider from "src/components/ScrollShadowProvider";
import { numberToStringWithComma } from "src/utils";
import CreditStatusTable from "./CreditStatusTable";

const fmt = (v?: number | string) => (v !== undefined && v !== null ? numberToStringWithComma(Number(v)) : "-");

interface IProps {
  contractId: string;
  bucket: ContractCreditBucket;
  onClose: () => void;
}

/**
 * 계약 충전금 관리 > 월별 충전금 내역 > 변경내역 모달 (RCL22)
 * - 충전금 현황(현재 버킷) + 충전금 변경내역(충전/회수 이력) 표시 (읽기 전용)
 * - 구분(지급/회수)은 chargedCredit 의 +/- 로 판단, 충전금은 절대값 표기
 */
const CreditChangeHistoryModal = ({ contractId, bucket, onClose }: IProps) => {
  const { executeAsync: getCreditCharges } = useApiOperation(getCreditChargesAsync);
  const [charges, setCharges] = useState<CreditCharge[]>([]);

  const fetchCharges = useCallback(async () => {
    const res = await getCreditCharges({ contractId, bucketId: bucket.bucketId });
    setCharges(res?.data?.data?.creditChargeList ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, bucket.bucketId]);

  useEffect(() => {
    fetchCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo<Array<any>>(
    () => [
      {
        Header: "일시",
        accessor: "chargedAt",
        width: 160,
        Cell: ({ value }: Cell<CreditCharge>) => <p className="text-center">{value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"}</p>,
      },
      {
        Header: "구분",
        accessor: "chargedCredit",
        id: "type",
        width: 80,
        Cell: ({ value }: Cell<CreditCharge>) => <p>{Number(value) >= 0 ? "지급" : "회수"}</p>,
      },
      {
        Header: "충전금",
        accessor: "chargedCredit",
        id: "amount",
        width: 200,
        Cell: ({ value }: Cell<CreditCharge>) => <p>{numberToStringWithComma(Math.abs(Number(value ?? 0)))}</p>,
      },
      {
        Header: "총 충전금",
        accessor: "cumulativeCredit",
        width: 200,
        Cell: ({ value }: Cell<CreditCharge>) => <p className="font-weight-medium">{fmt(value)}</p>,
      },
      {
        Header: "사유",
        accessor: "description",
        width: 280,
        Cell: ({ value }: Cell<CreditCharge>) => <BaseTooltip contents={value || "-"} isSingleLine className="w-100" />,
      },
      {
        Header: "작업자",
        accessor: "chargedBy",
        width: 240,
        Cell: ({ value }: Cell<CreditCharge>) => <p>{value || "-"}</p>,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({ columns, data: charges }, useBlockLayout, useSticky);
  const tableRef = useRef<HTMLDivElement>(null);
  const tableWidth = useMemo(() => columns.reduce((sum, c: any) => sum + Number(c.width || 0), 0), [columns]);

  return (
    <BaseAbstractModal isOpen>
      <>
        <section className="base-abstract-modal__title">
          <h1>{bucket.yearMonth} 충전금 변경내역</h1>
        </section>

        <section className="base-abstract-modal__contents px30">
          {/* 충전금 현황 */}
          <section className="base-abstract-modal__contents-subtitle">
            <h2>충전금 현황</h2>
          </section>
          <div className="mb30">
            <CreditStatusTable bucket={bucket} />
          </div>

          {/* 충전금 변경내역 */}
          <section className="base-abstract-modal__contents-subtitle">
            <h2>충전금 변경내역</h2>
          </section>
          <div className="base-table-wrap pl0" style={{ paddingRight: 0 }}>
            <ScrollShadowProvider tableRef={tableRef}>
              <div {...getTableProps()} className="base-table sticky" ref={tableRef}>
                <div className="header">
                  {headerGroups.map((headerGroup) => (
                    <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                      {headerGroup.headers.map((header) => (
                        <div {...header.getHeaderProps()} className="base-table__th">
                          {header.render("Header")}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div {...getTableBodyProps()} className="body">
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <div {...row.getRowProps()} className="base-table__tr">
                        {row.cells.map((cell) => (
                          <div {...cell.getCellProps()} className="base-table__td">
                            {cell.render("Cell")}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {rows.length === 0 && (
                    <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                      <div className="base-table__td w-100 text-center">
                        <div className="w-100 table-no-data">
                          <p>데이터가 없습니다.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollShadowProvider>
          </div>
        </section>

        <section className="base-abstract-modal__btn-wrap">
          <BaseButton title={"닫기"} className="color-white" onClick={onClose} />
        </section>
      </>
    </BaseAbstractModal>
  );
};

export default CreditChangeHistoryModal;
